import { useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash';
import uuid from 'react-uuid';
import { ParserParams } from 'common/types/scalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { SHEET_CONFIG_CONSTANTS } from 'pages/Portfolio/common/constants/companySummary';
import {
  CompanySummaryColumn,
  createColumns,
  customParser,
  rowConfig,
} from 'pages/Portfolio/pages/CompanySummary/config';
import { RowData } from 'pages/Portfolio/pages/CompanySummary/config/rowConfig';
import { CflBucket } from 'services/hooks/companies/useGetCompanySummaryByMD/types';
import { getArrayValue } from 'utilities';
import {
  CFLFundTotal,
  CFLTotalsByFundSlug,
  CFLTotalsByFundSlugBySecurity,
  CompanyFundInvestment,
  CompanySummaryInitialObject,
  FundInvestmentsWithId,
  FundsSecuritiesIndexes,
  UseCompanySummarySheet,
} from './types';

const { COMPANY_SUMMARY_SPREADSHEET_TABLE_TERMS } = SHEET_CONFIG_CONSTANTS;

const EXCLUDE_INDEX_AND_SUBHEADER_AND_SUBTOTAL = 3;

const bucketToTotal = (bucket: CflBucket | undefined): CFLFundTotal => ({
  cfl_invested: Number(bucket?.cfl_invested ?? 0) || 0,
  cfl_realized: Number(bucket?.cfl_realized ?? 0) || 0,
  cfl_total_value: Number(bucket?.cfl_total_value ?? bucket?.cfl_realized ?? 0) || 0,
});

const useCompanySummarySheet: UseCompanySummarySheet = params => {
  const {
    companySlug,
    funds = [],
    firmSlug,
    queryParams,
    cflTotalsByFundNoSecurity,
    cflTotalsByFundSecurity,
  } = params;

  const fundsSecuritiesWithId = useMemo(
    () =>
      getArrayValue(
        funds.map(fund => ({
          ...fund,
          investments: fund.investments?.map(investment => ({ ...investment, id: uuid() })),
        })) as FundInvestmentsWithId[]
      ),
    [funds]
  );

  const fundsInvestments = useMemo(
    () =>
      getArrayValue(
        fundsSecuritiesWithId?.reduce((accumulator, current) => {
          const { fund_slug: fundSlug, investments = [] } = current;

          const fundSecurities = getArrayValue(
            investments?.map(
              investment =>
                ({
                  ...investment,
                  fundSlug,
                } as CompanyFundInvestment)
            )
          );

          return [...accumulator, ...fundSecurities];
        }, [] as CompanyFundInvestment[])
      ),
    [fundsSecuritiesWithId]
  );

  // No-security slice drives the Fund Total / Firm Total rows. Per-security
  // CFL is already baked into the security subtotal rows (below) so this
  // slice intentionally excludes it. Falls back to the legacy fund-level
  // keys only when the new top-level field is absent (transitional safety
  // for stale cached responses) — backend always emits the new field.
  const cflTotalsByFundSlug: CFLTotalsByFundSlug = useMemo(() => {
    if (cflTotalsByFundNoSecurity) {
      return Object.fromEntries(
        Object.entries(cflTotalsByFundNoSecurity).map(([slug, bucket]) => [slug, bucketToTotal(bucket)])
      );
    }
    return (funds || []).reduce<CFLTotalsByFundSlug>((acc, fund) => {
      const slug = fund?.fund_slug;
      if (!slug) {
        return acc;
      }
      acc[slug] = bucketToTotal({
        cfl_invested: String(fund?.cfl_invested ?? 0),
        cfl_realized: String(fund?.cfl_realized ?? 0),
        cfl_total_value: String(fund?.cfl_total_value ?? fund?.cfl_realized ?? 0),
        cfl_x_factor: null,
      });
      return acc;
    }, {});
  }, [cflTotalsByFundNoSecurity, funds]);

  const cflTotalsByFundSlugBySecurity: CFLTotalsByFundSlugBySecurity = useMemo(() => {
    if (!cflTotalsByFundSecurity) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(cflTotalsByFundSecurity).map(([slug, perSecurity]) => [
        slug,
        Object.fromEntries(
          Object.entries(perSecurity).map(([securityIdKey, bucket]) => [securityIdKey, bucketToTotal(bucket)])
        ),
      ])
    );
  }, [cflTotalsByFundSecurity]);

  const fundsSecuritiesIndexes = useMemo(() => {
    let lastFundSlug = '';
    let lastIndex = 0;

    return fundsInvestments.reduce((accumulator, current) => {
      const { fundSlug: fundTotalKey = '' } = current;

      const fundRows = getArrayValue(accumulator?.[fundTotalKey]);

      if (lastFundSlug && fundTotalKey === lastFundSlug) {
        lastIndex += 1;
      } else {
        lastIndex += EXCLUDE_INDEX_AND_SUBHEADER_AND_SUBTOTAL;
      }

      // Update Last iterated Fund
      lastFundSlug = fundTotalKey;

      return {
        ...accumulator,
        [fundTotalKey]: [...fundRows, lastIndex],
      };
    }, {} as FundsSecuritiesIndexes);
  }, [fundsInvestments]);

  // Create row config for Investments Spreadsheet
  const config = useMemo(
    () => rowConfig({ companySlug, firmSlug, funds: fundsSecuritiesWithId, queryParams }),
    [companySlug, firmSlug, fundsSecuritiesWithId, queryParams]
  );

  // Create columns for Investments Spreadsheet
  const columns = useMemo(() => {
    if (!isEmpty(fundsInvestments)) {
      return createColumns({ fundsInvestments });
    }

    return [];
  }, [fundsInvestments]);

  // Create parser with custom props
  const parser = useCallback(
    (props: ParserParams<CompanySummaryColumn, CompanySummaryInitialObject, RowData>) =>
      customParser({
        ...props,
      }),
    []
  );

  // Create Spreadsheet config
  const spreadsheet = useMemo(() => {
    if (!isEmpty(columns)) {
      const spreadsheetConfig = new SpreadsheetConfig({
        allowConfirmAndDeleteColumn: false,
        alwaysDisplayLegend: false,
        colTitleRow: 0, // Add header row styles
        columns,
        currencyFormatter: false,
        format: undefined,
        hasColTitle: true, // Add styles to header row (colTitleRow)
        initialObj: {
          fundsSecuritiesIndexes,
          cflTotalsByFundSlug,
          cflTotalsByFundSlugBySecurity,
        } as unknown as SpreadsheetConfig['initialObj'],
        name: COMPANY_SUMMARY_SPREADSHEET_TABLE_TERMS.tableName,
        page: COMPANY_SUMMARY_SPREADSHEET_TABLE_TERMS.tableName as unknown as SpreadsheetConfig['page'],
        parser,
        rowConfig: config,
        showPreviousColsDivider: false,
        showTitlesColumn: true,
        showToolbar: true,
        showTotalColumn: false,
        tableData: { columns },
        tableTerms: COMPANY_SUMMARY_SPREADSHEET_TABLE_TERMS as unknown as SpreadsheetConfig['tableTerms'],
        totalParser: undefined,
        unitsFormatter: false,
      });

      return spreadsheetConfig;
    }

    return null;
  }, [columns, config, fundsSecuritiesIndexes, cflTotalsByFundSlug, cflTotalsByFundSlugBySecurity, parser]);

  return {
    spreadsheet,
  };
};

export default useCompanySummarySheet;
